import React, { useEffect, useState, useCallback, useContext } from 'react';
import './Contract.scss';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import MultiSwitch from '../../Components/MultiSwitch/MultiSwitch';
import Anonymisation from './Anonymisation';
import Analysis from './Analysis';
import { ReactSVG } from 'react-svg';
import privacy from '../../Assets/icons/privacy.svg';
import review from '../../Assets/icons/review.svg';
import clock from '../../Assets/icons/clock.svg';
import { useAuth0 } from '@auth0/auth0-react';
import Copilot from './Copilot';
import { useTranslation } from 'react-i18next';
import ContractEdit from './ContractEdit';
import Breadcrumb from '../../Components/Breadcrumb/Breadcrumb';
import fly from '../../Assets/icons/owl.png';
import SupportButton from '../../Components/Support/SupportButton';
import ApiContext from '../../Api/ApiContext';
import LottieAnimation from '../../Components/Animation/LottieAnimation';
import contractAnimation from '../../Assets/lottie/loading.json';
import { useSelector } from 'react-redux';
import { selectResellerConfig } from '../../Store/configSlice';
import Upload from '../../Components/Upload/Upload';
import RestrictReadOnly from '../../Helpers/RestrictReadOnly';
import AccessDeniedScreen from '../../Components/Misc/AccessDeniedScreen';
import DOMPurify from "dompurify";

type ContractProps = {
  selectedTab: number;
  lang: string;
};

type ContractData = {
  name?: string;
  type?: string;
  time_saved?: number;
  status?: string;
  party?: string;
  language?: string;
  jurisdiction?: string;
  content?: string;
  redacted_content?: string;
  features?: {
    anonymisation?: boolean;
  };
};

const Contract: React.FC<ContractProps> = ({ selectedTab, lang }) => {

  const { i18n } = useTranslation();
  const [t, setT] = useState(() => key => key || "");

  const resellerConfig = useSelector(selectResellerConfig);

  useEffect(() => {
    setT(() => i18n.getFixedT(lang));
  }, [lang, i18n]);

  const { t: tGlobal } = useTranslation();

  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const api = useContext(ApiContext);

  const params = useParams();
  const urlId = params.id;

  const isReseller = process.env.REACT_APP_IS_RESELLER === "true";

  const id = (() => {
    if (isReseller) {
      if (resellerConfig?.legalFlyId) {
        return resellerConfig.legalFlyId.toString();
      } else {
        return null;
      }
    }
    return urlId;
  })();

  const options = [ <><img src={fly} alt="Fly" />{tGlobal('tabs.copilot')}</>, <><ReactSVG src={review} />{tGlobal('tabs.review')}</>, <><ReactSVG src={privacy} />{tGlobal('tabs.anon')}</>];
  const [ option, setOption ] = useState(selectedTab);

  const [ contract, setContract ] = useState<ContractData>({});
  const [ contractContent, setContractContent ] = useState("");

  const [ status, setStatus ] = useState("");

  function mapLanguage(language) {
    switch(language) {
      case "English":
        return "en";
      case "Français":
        return "fr";
      case "Nederlands":
        return "nl";
      case "Deutsch":
        return "de";
      default:
        return "en";
    }
  }

  useEffect(() => {

      let interval = null;

      if(!id) return;

      api.post('/document', 
        { documentId: id }
      ).then(res => {
        setContract(res.data);
        if(!process.env.REACT_APP_IS_RESELLER) {
          setT(() => i18n.getFixedT(mapLanguage(res.data.language)));
        }
        setContractContent(res.data.redacted_content ?? res.data.content);

        interval = setInterval(fetchDocument, 5000);
      });
    
      const fetchDocument = async () => {
        try {
          const { data } = await api.post('/document', 
            { documentId: id }
          );

          setContract(prevContract => {
            if(JSON.stringify(prevContract) !== JSON.stringify(data)) {
              return data;
            }
            return prevContract;
          });
      
          setContractContent(prevContent => {
            const newContent = data.redacted_content ?? data.content;
            if(prevContent !== newContent) {
              return newContent;
            }
            return prevContent;
          });

          if(data.status === "VALIDATED") clearInterval(interval);

        } catch (error) {
          console.error(error);
        }
      };
    
      return () => {
        clearInterval(interval);
      };
  }, [getAccessTokenSilently, id, i18n, api]);

  const { pathname } = useLocation();

  useEffect(() => {
    const navigateTo = (path) => {
      if (pathname !== path) {
        navigate(path);
      }
    };
  
    if (option === 0) navigateTo(`/review/${urlId}/copilot`);
    if (option === 1) navigateTo(`/review/${urlId}`);
    if (option === 2) navigateTo(`/review/${urlId}/anonymisation`);

    Array.from(document.getElementsByClassName('highlighted')).forEach(element => {
      element.classList.remove('highlighted');
    });
  }, [option, id, navigate]);

  const [partyState, setPartyState] = useState(null);
  const [typeState, setTypeState] = useState(null);
  const [languageState, setLanguageState] = useState(null);
  const [jurisdictionState, setJurisdictionState] = useState(null);
  const [ checklist, setChecklist ] = useState([]);
  const [ selectedTrainingDocuments, setSelectedTrainingDocuments ] = useState([]);
  const [ trainingFilter, setTrainingFilter ] = useState([]);
  const [ showTrainingModal, setShowTrainingModal ] = useState(false);
  const [ reviewing, setReviewing ] = useState(Boolean(contract?.party));

  useEffect(() => {
    if(contract) {
      if(!Boolean(contract?.party)) {
        setStatus("Review Pending");
        if(!Boolean(typeState))
          setTypeState(contract.type);
        if(!Boolean(languageState))
          setLanguageState(process.env.REACT_APP_IS_RESELLER ? i18n.language : contract.language);
        if(!Boolean(jurisdictionState))
          setJurisdictionState(contract.jurisdiction);
        return;
      } else {
        setReviewing(true);
      }
      
      if(contract.status === "VALIDATED") {
        setStatus("Completed");
      } else {
        setStatus("In Progress");
      }
    }
  }, [contract, typeState, languageState, jurisdictionState]);

  const Header = () => {

      return (
        <div className="contractHeader">
          <div className="left">
            <Breadcrumb pageName={contract?.name} id={id} />
            <SupportButton subject={id} feature="review" />
          </div>
          <div className="right">
            <MultiSwitch options={options} defaultOption={option} setOption={setOption} type="tabs" />
          </div>
      </div>
      );
  }

  const [ copilotPrompt, setCopilotPrompt ] = useState("");

  function navigateToCopilot(prompt) {
    setOption(0);
    setCopilotPrompt(prompt);
  }

  const [selection, setSelection] = useState('');
  const [showButton, setShowButton] = useState(false);

  const Result = () => {

    return (
      <>
        <Copilot 
          documentId={id} 
          prompt={copilotPrompt} 
          setCopilotPrompt={setCopilotPrompt} 
          display={option === 0} 
          copyToEditor={copyToEditor}
          t={t}
          reviewing={reviewing}
          typeState={typeState}
        />
        <Analysis 
          id={id} 
          navigateToCopilot={navigateToCopilot} 
          display={option === 1} 
          doc={contract}
          partyState={partyState}
          setPartyState={setPartyState}
          typeState={typeState}
          setTypeState={setTypeState}
          languageState={languageState}
          setLanguageState={setLanguageState}
          jurisdictionState={jurisdictionState}
          setJurisdictionState={setJurisdictionState}
          checklist={checklist}
          setChecklist={setChecklist}
          selectedTrainingDocuments={selectedTrainingDocuments}
          setSelectedTrainingDocuments={setSelectedTrainingDocuments}
          trainingFilter={trainingFilter}
          setTrainingFilter={setTrainingFilter}
          showTrainingModal={showTrainingModal}
          setShowTrainingModal={setShowTrainingModal}
          reviewing={reviewing}
          setReviewing={setReviewing}
          t={t}
          setT={setT}
        />
        <Anonymisation 
          id={id} 
          display={option === 2} 
          selection={selection} 
          showButton={showButton}
          setSelection={setSelection}
          setShowButton={setShowButton}
          setContractContent={setContractContent}
          t={t}
        />
      </>
    )
  }

  function decodeHtml(html) {
      var txt = document.createElement("textarea");
      txt.innerHTML = html;
      return txt.value;
  }

  const handleMouseUp = () => {
    if(option !== 2 || !contract.features.anonymisation) return;
    const text = window.getSelection().toString();

    if(text.length === 0) return;

    const updatedContent = removeTmpRedacteds();
    
    const replacement = `<span class="redacted tmp"><span class="type">${text}</span></span>`;
    const escapedText = text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); 
    const regex = new RegExp(escapedText, 'g');
    const newContractContent = updatedContent.replace(regex, replacement);
    setContractContent(newContractContent);

    setSelection(text);
    if (text) {
        setShowButton(true);
    } else {
        setShowButton(false);
    }
  };

  const removeTmpRedacteds = () => {
    let modifiedContent = contractContent;
    const regex = /<span class="redacted tmp"><span class="type">(.*?)<\/span><\/span>/gi;
  
    modifiedContent = modifiedContent.replace(regex, (match, group1) => {
      return group1;
    });
  
    return modifiedContent;
  };

  const [ textToCopy, setTextToCopy ] = useState("");

  const copyToEditor = useCallback((contentToPaste) => {
    setTextToCopy(contentToPaste);
  }, []);

  const replaceTypeWithTranslation = (content) => {
    const regex = /<span class="type">(.*?)<\/span>/g;
    const replacedContent = content.replace(regex, (match, type) => {
      const translatedType = t(type); // Get the translated value of the type
      return `<span class="type">${translatedType}</span>`;
    });
    return replacedContent;
  };

  const decodedContent = decodeHtml(contractContent);
  const replacedContent = replaceTypeWithTranslation(decodedContent);

  const ContractContent = () => {

    const hoursSaved = Math.floor(contract?.time_saved / 60);
    const minutesSaved = (contract?.time_saved % 60);
  
    return (
      <>
        <div className={"text-editor " + ((option === 0 && !isReseller) ? "show" : "hide")}>
          <ContractEdit documentId={id} documentName={contract.name} textToCopy={textToCopy} setTextToCopy={setTextToCopy} />
        </div>
        <div className={"contract " + ((option !== 0 || isReseller) ? "show" : "hide")}>
          <div className="documentHeader">
              { status === "Completed" && <div className="time">
                    <ReactSVG src={clock} />
                    {tGlobal('timeSaved', { hoursSaved, minutesSaved })}
                </div> 
            }
          </div>
          <div className="contractContent" id="contractContent" onMouseUp={handleMouseUp}>
            <div dangerouslySetInnerHTML={{ __html:  DOMPurify.sanitize(decodeHtml(replacedContent)) }} />
          </div>
        </div>
      </>
    );
  }
  
  if (resellerConfig) {
    if (!resellerConfig?.access){
      return <AccessDeniedScreen />
    } else if (!resellerConfig.legalFlyId) {
      return <ResellerUpload />;
    }
  }

  return (
    <div className={"result " + (option === 2 ? "anon" : "")}>
      {Header()}
      {ContractContent()}
      {Result()}
    </div>
  )
}

const ResellerUpload = () => {
  const [dragCounter, setDragCounter] = useState(0);

  const onDragEnterHandler = useCallback((e) => {
      if (e.dataTransfer.types.includes("text/plain")) {
          setDragCounter(prev => prev + 1);
      }
  }, []);
  
  const onDragLeaveHandler = useCallback(() => {
      setDragCounter(prev => prev > 0 ? prev - 1 : 0);
  }, []);
  
  const isDragHover = dragCounter > 0;

  const { t } = useTranslation();

  return (
    <div className="reseller-uploader-wrapper"
      onDragEnter={onDragEnterHandler}
      onDragLeave={onDragLeaveHandler}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        setDragCounter(0);
      }}
    >
      <div className='reseller-uploader'>
        <div className="icon">
          <LottieAnimation animationData={contractAnimation} />
        </div>
        <RestrictReadOnly>
          <h3>{t('reseller.upload.title')}</h3>
          <p>{t('reseller.upload.info')}</p>
          <Upload update={(args) => { console.log({ args })}} anonymise={true} hovering={isDragHover} type="review" />
        </RestrictReadOnly>
      </div>
    </div>
  )
}

export default Contract;